import React from 'react'
import { Avatar, Typography } from '@mui/material'
import Fab from 'components/Fab'
import Condition from 'components/Condition'
import LoadingIcon from 'components/LoadingIcon'
import { circleAvatarOptions } from 'services/filestack'
import { Create } from '@mui/icons-material'
import { useUploadFile } from 'hooks/filestack'
import { graphql, useFragment } from 'react-relay'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { AvatarField_UpdateUserMutation } from '__generated__/AvatarField_UpdateUserMutation.graphql'
import { useSnackbar } from 'hooks/redux'
import { AvatarField_User$key } from '__generated__/AvatarField_User.graphql'
import Picture from 'components/Picture'
import useStyles from './styles'

export const PROFILE_AVATAR_FIELD_ID = 'profile-avatar-field'

export interface IAvatarFieldProps {
  user: AvatarField_User$key
  editable: boolean
}

function AvatarField(props: IAvatarFieldProps) {
  const { classes } = useStyles()
  const { editable, user: userFragmentRef } = props
  const { showSnackbarMessage } = useSnackbar()

  const user = useFragment(graphql`
    fragment AvatarField_User on User {
      avatarUrl
    }
  `, userFragmentRef)

  const { execute, loading } = useQuickCommit<AvatarField_UpdateUserMutation>(graphql`
    mutation AvatarField_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          avatarUrl
        }
      }
    }
  `)

  const filestack = useUploadFile({
    onChange: (file) => {
      execute({
        input: {
          avatarUrl: file.url,
        },
      }).then((result) => {
        if (result?.updateUser?.user?.avatarUrl) {
          showSnackbarMessage('Avatar updated', { level: 'success' })
        }
      })
    },
    options: circleAvatarOptions(),
  })

  const error = filestack.error
  const hasError = Boolean(error)
  const avatarUrl = user?.avatarUrl || 'https://flexhire-images.s3.amazonaws.com/images/no_avatar.png'

  return (
    <div className={classes.wrapper}>
      <div className={classes.container} id={PROFILE_AVATAR_FIELD_ID}>
        <Avatar
          className={classes.avatar}
        >
          <Picture width={300} src={avatarUrl} style={{ width: '100%', display: 'block' }} />
        </Avatar>

        <Condition condition={editable}>
          <Fab
            color={hasError ? 'secondary' : 'primary'}
            className={classes.fab}
            onClick={filestack.openPicker}
            disabled={filestack.isLoading || filestack.isOpen || loading}
            data-cy="edit-avatar"
          >
            {(filestack.isLoading || loading) ? <LoadingIcon /> : <Create />}
          </Fab>
        </Condition>

        <Condition condition={hasError}>
          <Typography variant="body2" className={classes.error}>
            {error && 'Failed to upload image'}
          </Typography>
        </Condition>
      </div>
    </div>
  )
}

export default React.memo(AvatarField)
